import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Map, { Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Video,
  Users,
  Box,
  Calendar as CalendarIcon,
  Search,
  MapPin,
  Clock,
  Camera,
  Video as VideoIcon,
  MoreHorizontal,
  CalendarDays,
  X,
  ChevronRight,
  Filter,
  CheckCircle2,
  FileText,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { useTheme } from '../context/ThemeContext';
import { useCrew } from '../context/CrewContext';
import { useEquipment } from '../context/EquipmentContext';
import { getActiveProjects } from '../lib/api/requirementsApi';
import { fetchRecentActivity, fetchUpcomingEvents, fetchProjectStats } from '../lib/api/dashboardApi';
import { getAllCrew } from 'services/crewService';
import { getAllEquipment } from 'services/equipmentService';

import PageTitle from 'components/generic/PageTitle';
import { startOfDay } from 'date-fns';

// Legend configuration with colors and filters
//Active Productions
//Upcoming Shoots
//Cancelled Shoots
//Completed Shoots
const legendConfig = [
  { id: 'active_events', label: 'Active Productions', color: 'bg-green-500', borderColor: 'border-green-500', filter: { status: 'active', type: 'event' }, statKey: 'total_active' },
  { id: 'upcoming_events', label: 'Upcoming Shoots', color: 'bg-blue-400', borderColor: 'border-blue-400', filter: { status: 'upcoming', type: 'event' }, statKey: 'total_upcoming' },
  // { id: 'draft_events', label: 'Draft Events', color: 'bg-yellow-500', borderColor: 'border-yellow-500', filter: { status: 'draft', type: 'event' }, statKey: 'total_draft' }, // New draft filter

  ///{ id: 'cancelled_events', label: 'Cancelled Shoots', color: 'bg-red-500', borderColor: 'border-red-500', filter: { status: 'cancelled', type: 'event' }, statKey: 'total_cancelled' },
  ///{ id: 'completed_events', label: 'Completed Shoots', color: 'bg-indigo-600', borderColor: 'border-indigo-600', filter: { status: 'completed', type: 'event' }, statKey: 'total_completed' },
];

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "pk.eyJ1IjoiZXhhbXBsZSIsImEiOiJja2xsZ...replace_with_your_token";

const getMarkerColor = (status) => {
  switch (status) {
    case 'active': return { bg: 'bg-green-500', border: 'border-green-500' };
    case 'completed': return { bg: 'bg-indigo-600', border: 'border-indigo-600' };
    case 'cancelled': return { bg: 'bg-red-500', border: 'border-red-500' };
    case 'upcoming': return { bg: 'bg-blue-400', border: 'border-blue-400' };
    case 'assigned': return { bg: 'bg-yellow-500', border: 'border-yellow-500' };
    case 'traveling': return { bg: 'bg-blue-400', border: 'border-blue-400' };
    case 'draft': return { bg: 'bg-yellow-500', border: 'border-yellow-500' };
    default: return { bg: 'bg-gray-500', border: 'border-gray-500' };
  }
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { crewMembers, fetchAllCrew, loading: crewLoading } = useCrew();
  const { equipment, stats: equipmentStats, fetchAllEquipment, loading: equipmentLoading } = useEquipment();
  const eventsSectionRef = useRef(null);
  const [viewState, setViewState] = useState({
    latitude: 39.8283,
    longitude: -98.5795,
    zoom: 1.5,
    pitch: 0,
    bearing: 0
  });

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeFilters, setActiveFilters] = useState(new Set(legendConfig.map(l => l.id)));
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [highlightedLegend, setHighlightedLegend] = useState(null);
  const [dbEvents, setDbEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const initialZoomDone = useRef(false);
  const [crewCount, setCrewCount] = useState(0);
  const [equipmentCount, setEquipmentCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);

  // Map filter states
  const [eventFilter, setEventFilter] = useState('active');
  const [crewFilter, setCrewFilter] = useState('all_crew');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);

  // Upcoming Events states
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingEventsLoading, setUpcomingEventsLoading] = useState(true);
  const [upcomingEventsError, setUpcomingEventsError] = useState(null);
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [eventCategoryFilter, setEventCategoryFilter] = useState('all');
  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('');

  // Recent Activity states
  const [recentActivityData, setRecentActivityData] = useState([]);
  const [recentActivityLoading, setRecentActivityLoading] = useState(true);
  const [recentActivityError, setRecentActivityError] = useState(null);

  // Project Stats states
  const [projectStats, setProjectStats] = useState({
    total_active: 0,
    total_cancelled: 0,
    total_completed: 0,
    total_upcoming: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Calculate dynamic legend items with counts from actual data
  const legendItems = legendConfig.map(config => {
    let count = 0;
    if (projectStats && config.statKey) {
      count = projectStats[config.statKey] || 0;
    } else {
      const matchingEvents = dbEvents.filter(event =>
        event.status === config.filter.status &&
        event.type === config.filter.type &&
        event.hasCoordinates
      );
      count = matchingEvents.length;
    }

    return {
      ...config,
      count: count
    };
  });

  const isValidToken = MAPBOX_TOKEN && !MAPBOX_TOKEN.includes("replace_with_your_token");

  // Helper to geocode address (restricted to USA)
  const geocodeAddress = async (address) => {
    if (!address || !isValidToken) return null;
    try {
      // Restrict to USA and bias toward US center coordinates
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&country=us&proximity=-98.5795,39.8283&limit=1&types=place,locality,region`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        const placeName = data.features[0].place_name;
        console.log(`Geocoded "${address}" to ${placeName} (${lat}, ${lng})`);
        return { lat, lng, placeName };
      } else {
        console.warn(`No geocoding results found for "${address}"`);
      }
    } catch (error) {
      console.error("Geocoding error for", address, ":", error);
    }
    return null;
  };

  // Get events matching a specific legend filter (using only database events)
  const getEventsForLegend = useCallback((legend) => {
    return dbEvents.filter(event =>
      event.status === legend.filter.status && event.type === legend.filter.type && event.hasCoordinates
    );
  }, [dbEvents]);

  // Calculate bounding box and zoom to fit all markers for a legend
  const zoomToLegendMarkers = useCallback((legend) => {
    const matchingEvents = getEventsForLegend(legend);
    console.log(`Legend clicked: ${legend.label} (${legend.id})`);
    console.log(`- Filter:`, legend.filter);
    console.log(`- Matching events:`, matchingEvents.length);
    console.log(`- Events:`, matchingEvents.map(e => ({ title: e.title, status: e.status, hasCoordinates: e.hasCoordinates })));

    if (matchingEvents.length === 0) {
      console.warn(`No events with coordinates found for legend: ${legend.label}`);
      return;
    }

    // Highlight this legend category
    setHighlightedLegend(legend.id);

    // Toggle filter: click to show only this category, click again to show all
    setActiveFilters(prev => {
      console.log(`Previous activeFilters:`, Array.from(prev));
      // If only this legend is active, reset to show all
      if (prev.size === 1 && prev.has(legend.id)) {
        console.log(`Resetting to show all categories`);
        return new Set(legendConfig.map(l => l.id));
      }
      // Otherwise, show only this legend
      console.log(`Setting activeFilters to only:`, legend.id);
      return new Set([legend.id]);
    });

    if (matchingEvents.length === 1) {
      // Single marker - zoom directly to it
      setViewState(prev => ({
        ...prev,
        latitude: matchingEvents[0].lat,
        longitude: matchingEvents[0].lng,
        zoom: 8,
        pitch: 0,
        bearing: 0
      }));
      setSelectedEvent(matchingEvents[0]);
    } else {
      // Multiple markers - calculate bounds and fit
      const lats = matchingEvents.map(e => e.lat);
      const lngs = matchingEvents.map(e => e.lng);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;

      // Calculate zoom level based on bounds - adjusted for US-wide locations
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      let zoom = 4;
      if (maxDiff < 1) zoom = 8;
      else if (maxDiff < 5) zoom = 6;
      else if (maxDiff < 10) zoom = 5;
      else if (maxDiff < 20) zoom = 4;
      else if (maxDiff < 40) zoom = 3.5;
      else zoom = 3;

      setViewState(prev => ({
        ...prev,
        latitude: centerLat,
        longitude: centerLng,
        zoom: zoom,
        pitch: 0,
        bearing: 0
      }));
      setSelectedEvent(null);
    }

    // Clear highlight after animation
    setTimeout(() => setHighlightedLegend(null), 2000);
  }, [getEventsForLegend]);

  // Auto-zoom to first non-empty legend category on initial load or when geocoding completes
  useEffect(() => {
    const hasCoordinates = dbEvents.some(e => e.hasCoordinates);

    if (dbEvents.length > 0 && hasCoordinates && !initialZoomDone.current) {
      setViewState(prev => ({
        ...prev,
        latitude: 39.8283,
        longitude: -98.5795,
        zoom: 3.0,
        pitch: 0,
        bearing: 0
      }));

      setSelectedEvent(null);

      initialZoomDone.current = true;
    }
  }, [dbEvents]);

  // Helper function to map activity type to icon and styling
  const getActivityIcon = (type) => {
    const iconMap = {
      'requirement': { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
      'equipment': { icon: Box, color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
      'crew': { icon: Users, color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
      'project': { icon: Video, color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
      'booking': { icon: CalendarIcon, color: 'text-pink-500', bg: 'bg-pink-50 dark:bg-pink-900/20' },
      'default': { icon: Activity, color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-900/20' }
    };
    return iconMap[type] || iconMap['default'];
  };

  // Fetch active projects from database
  const fetchActiveProjects = async () => {
    try {
      setEventsLoading(true);
      const response = await getActiveProjects();

      if (response.error === false && response.data) {
        console.log("Active projects API response:", response.data.length, "projects");

        // Transform database projects to map event format
        // Include ALL projects, not just those with coordinates
        const transformedEvents = response.data.map(project => {
            // Safely parse lat/lng as floats if they exist
            const rawLat = project.location_data?.lat;
            const rawLng = project.location_data?.lng;

            const lat = rawLat ? parseFloat(rawLat) : null;
            const lng = rawLng ? parseFloat(rawLng) : null;

            const hasCoordinates = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng);

            // Categorize project status for legend filtering
            // Priority: cancelled > completed > upcoming > active
            let projectStatus = 'active'; // default

            // Debug specific project
            if (project.project_name && project.project_name.toLowerCase().includes('tech kick off')) {
              console.log(`DEBUG: ${project.project_name}:`, {
                is_cancelled: project.is_cancelled,
                is_completed: project.is_completed,
                is_active: project.is_active,
                is_draft: project.is_draft,
                event_date: project.event_date
              });
            }

            if (project.is_cancelled === 1) {
              projectStatus = 'cancelled';
            } else if (project.is_completed === 1) {
              projectStatus = 'completed';

            }
            else if (project.is_draft === 1) {
              projectStatus = 'draft';
            }else if (project.event_date) {
              const eventDate = new Date(project.event_date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              if (eventDate > today) {
                projectStatus = 'upcoming';
              } else if (project.is_active === 1) {
                projectStatus = 'active';
              }
            } else if (project.is_active === 1) {
              projectStatus = 'active';
            }

            // Debug result
            if (project.project_name && project.project_name.toLowerCase().includes('tech kick off')) {
              console.log(`  -> Categorized as: ${projectStatus}`);
            }

          return {
            id: project.stream_project_booking_id,
            title: project.project_name,
            date: project.event_date ? new Date(project.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
            rawDate: project.event_date, // Keep raw date for filtering
            time: `${project.duration_hours || 0} hours`,
            location: hasCoordinates
              ? (project.location_data.address || project.location_data.placeName || 'Location')
              : (project.event_location || 'Location TBD'),
            lat: lat,
            lng: lng,
            status: projectStatus,
            type: 'event',
            equipment: [],
            requirements: `${project.crew_size_needed || 0}/N`,
            updated: 'Today',
            description: project.description,
            event_type: project.event_type,
            budget: project.budget,
            expected_viewers: project.expected_viewers,
            hasCoordinates: hasCoordinates,
            assignedCrewCount: project.assignedCrewCount || 0,
            // Keep original fields for reference
            is_active: project.is_active,
            is_cancelled: project.is_cancelled,
            is_completed: project.is_completed,
            is_draft: project.is_draft
          };
        });

        console.log("Transformed projects:", transformedEvents.length);
        console.log("Projects with coordinates:", transformedEvents.filter(p => p.hasCoordinates).length);
        console.log("Projects without coordinates:", transformedEvents.filter(p => !p.hasCoordinates).length);

        // Debug: Check status distribution
        console.log("Status distribution:");
        console.log("- Active:", transformedEvents.filter(p => p.status === 'active').length);
        console.log("- Upcoming:", transformedEvents.filter(p => p.status === 'upcoming').length);
        console.log("- Cancelled:", transformedEvents.filter(p => p.status === 'cancelled').length);
        console.log("- Completed:", transformedEvents.filter(p => p.status === 'completed').length);
        console.log("- Active with coordinates:", transformedEvents.filter(p => p.status === 'active' && p.hasCoordinates).length);
        console.log("- Upcoming with coordinates:", transformedEvents.filter(p => p.status === 'upcoming' && p.hasCoordinates).length);
        console.log("- Cancelled with coordinates:", transformedEvents.filter(p => p.status === 'cancelled' && p.hasCoordinates).length);
        console.log("- Completed with coordinates:", transformedEvents.filter(p => p.status === 'completed' && p.hasCoordinates).length);

        setDbEvents(transformedEvents);

        // Attempt to geocode projects without coordinates but with a location string
        const eventsToGeocode = transformedEvents.filter(e => !e.hasCoordinates && e.location && e.location !== 'Location TBD');

        if (eventsToGeocode.length > 0) {
          console.log(`Attempting to geocode ${eventsToGeocode.length} projects...`);

          // Process geocoding in background
          const geocodePromises = eventsToGeocode.map(async (event) => {
            const coords = await geocodeAddress(event.location);
            if (coords) {
              return { id: event.id, ...coords };
            }
            return null;
          });

          const results = await Promise.all(geocodePromises);
          const successfulGeocodes = results.filter(r => r !== null);

          if (successfulGeocodes.length > 0) {
            console.log(`Successfully geocoded ${successfulGeocodes.length} projects`);

            setDbEvents(prevEvents => {
              return prevEvents.map(event => {
                const geocoded = successfulGeocodes.find(g => g.id === event.id);
                if (geocoded) {
                  return {
                    ...event,
                    lat: geocoded.lat,
                    lng: geocoded.lng,
                    location: geocoded.placeName || event.location,
                    hasCoordinates: true,
                    isGeocoded: true
                  };
                }
                return event;
              });
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching active projects:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  // Fetch project stats
  const loadProjectStats = async () => {
    try {
      setStatsLoading(true);
      const response = await fetchProjectStats();
      if (response.error === false && response.data) {
        setProjectStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching project stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };
  const handleStatusFilterChange = (selectedStatus) => {
  // Update the selected status filter
  setEventStatusFilter(selectedStatus);

  // Call the API with the updated status filter
  loadUpcomingEvents(selectedStatus);
};

// const loadUpcomingEvents = async (status) => {
//   try {
//     setUpcomingEventsLoading(true);
//     setUpcomingEventsError(null);

//     const filters = {
//       search: debouncedEventSearch,
//       category: eventCategoryFilter,
//       status: status || eventStatusFilter, // Use the selected status or fallback to the current filter
//     };

//     const response = await fetchUpcomingEvents(filters);

//     if (response.error === false && response.data) {
//       setUpcomingEvents(response.data.projects);  // Adjust based on your API response
//     } else {
//       setUpcomingEventsError('Failed to load upcoming events');
//     }
//   } catch (error) {
//     console.error('Error fetching upcoming events:', error);
//     setUpcomingEventsError(error.message || 'Failed to load upcoming events');
//   } finally {
//     setUpcomingEventsLoading(false);
//   }
// };


  // Fetch upcoming events with filters
  const loadUpcomingEvents = async () => {
  try {
    setUpcomingEventsLoading(true);
    setUpcomingEventsError(null);

    const filters = {
      search: debouncedEventSearch,
      category: eventCategoryFilter,
      status: eventStatusFilter
    };

    const response = await fetchUpcomingEvents(filters);

    if (response.error === false && response.data) {
      const events = response.data.projects.map((project) => {
        return {
          stream_project_booking_id: project.project.stream_project_booking_id,
          project_name: project.project.project_name,
          event_date: project.project.event_date,
          duration_hours: project.project.duration_hours,
          event_location: project.project.event_location,
          status: project.project.is_active ? 'Active' : 'Inactive',
          assignedCrew: project.assignedCrew,
          assignedEquipment: project.assignedEquipment,
        };
      });

      setUpcomingEvents(events);
    } else {
      setUpcomingEventsError('Failed to load upcoming events');
    }
  } catch (error) {
    console.error('Error fetching upcoming events:', error);
    setUpcomingEventsError(error.message || 'Failed to load upcoming events');
  } finally {
    setUpcomingEventsLoading(false);
  }
};


  // Fetch recent activity
  const loadRecentActivity = async () => {
    try {
      setRecentActivityLoading(true);
      setRecentActivityError(null);

      const response = await fetchRecentActivity();

      if (response.error === false && response.data) {
        setRecentActivityData(response.data);
      } else {
        setRecentActivityError('Failed to load recent activity');
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setRecentActivityError(error.message || 'Failed to load recent activity');
    } finally {
      setRecentActivityLoading(false);
    }
  };

  // Debounce search input for upcoming events
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEventSearch(eventSearchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [eventSearchQuery]);

  // Fetch upcoming events when filters change
  useEffect(() => {
    loadUpcomingEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEventSearch, eventCategoryFilter, eventStatusFilter]);

  // Handle "Today" button click
  const handleTodayFilter = () => {
    const today = startOfDay(new Date());
    // Filter upcoming events to today's date
    const todayEvents = upcomingEvents.filter(event => {
      if (!event.event_date) return false;
      const eventDate = startOfDay(new Date(event.event_date));
      return eventDate.getTime() === today.getTime();
    });

    // Update the displayed events
    setUpcomingEvents(todayEvents);
  };

  useEffect(() => {
    const fetchCrewCount = async () => {
      try {
        const response = await getAllCrew();

        if (response.error === null && response.pagination) {
          setCrewCount(response.pagination.total_records);
        } else {
          console.error('Error fetching crew count:', response.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error fetching crew count:', error);
      }
    };

    fetchCrewCount();
  }, []);


  useEffect(() => {
    const fetchEquipmentCount = async () => {
      try {
        const response = await getAllEquipment();
        console.log('Crew members API response:', response);

        if (response.error === null && response.summary) {
          setEquipmentCount(response.summary.total_equipment);
        } else {
          console.error('Error fetching crew count:', response.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error fetching crew count:', error);
      }
    };

    fetchEquipmentCount();
  }, []);

  useEffect(() => {
    const fetchEquipmentCount = async () => {
      try {
        const response = await getAllEquipment();
        console.log('Crew members API response:', response);

        if (response.error === null && response.summary) {
          setAvailableCount(response.summary.available_equipment);
        } else {
          console.error('Error fetching crew count:', response.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Error fetching crew count:', error);
      }
    };

    fetchEquipmentCount();
  }, []);


  useEffect(() => {
    fetchAllCrew();
    fetchAllEquipment();
    fetchActiveProjects();
    loadUpcomingEvents();
    loadRecentActivity();
    loadProjectStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate dynamic stats from context data
  const stats = [
    {
      label: 'Total Projects',
      value: eventsLoading ? '...' : dbEvents.length.toString(),
      icon: Video,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      onClick: () => {
        if (eventsSectionRef.current) {
          eventsSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    },
    {
      label: 'Crew Members',
      value: crewLoading ? '...' : crewCount.toString(),
      icon: Users,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      onClick: () => navigate('/crew-management')
    },
    {
      label: 'Equipment Items',
      value: equipmentLoading ? '...' : equipmentCount.toString(),
      icon: Box,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      onClick: () => navigate('/equipment')
    },
    {
      label: 'Available Equipment',
      value: equipmentLoading ? '...' : availableCount.toString(),
      icon: CalendarIcon,
      color: 'text-pink-600',
      bg: 'bg-pink-50',
      onClick: () => navigate('/equipment')
    },
  ];


  // Select all filters
  const selectAllFilters = useCallback(() => {
    setActiveFilters(new Set(legendConfig.map(l => l.id)));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setActiveFilters(new Set());
  }, []);

  const filteredEvents = dbEvents.filter(event => {
  // Must have coordinates
  if (!event.hasCoordinates) return false;

  // --- EVENT TYPE FILTER (Brand, Product, Lifestyle, etc.)
  if (eventFilter !== "active" && eventFilter !== "all") {
    if (event.event_type?.toString() !== eventFilter.toString()) {
      return false;
    }
  }

  // Legend filter
  const matchesLegend = legendItems.some(legend => {
    if (!activeFilters.has(legend.id)) return false;
    return event.status === legend.filter.status && event.type === legend.filter.type;
  });
  if (!matchesLegend) return false;

  // Search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    const matchesTitle = event.title?.toLowerCase().includes(query);
    const matchesLocation = event.location?.toLowerCase().includes(query);
    if (!matchesTitle && !matchesLocation) return false;
  }

  // Crew filters (same as your original)
  if (crewFilter === 'assigned') {
    if (!event.assignedCrewCount || event.assignedCrewCount === 0) return false;
  }

  if (crewFilter === 'cancelled' && event.is_cancelled !== true) return false;
  if (crewFilter === 'completed' && event.is_completed !== true) return false;
  if (crewFilter === 'draft' && event.is_draft !== true) return false;

  if (crewFilter === 'upcoming') {
    const eventDate = new Date(event.rawDate);
    if (eventDate <= new Date()) return false;
  }

  // Date filter
  if (selectedDate) {
    try {
      const eventDate = new Date(event.rawDate);
      const filterDate = new Date(selectedDate);
      if (eventDate.toDateString() !== filterDate.toDateString()) return false;
    } catch {}
  }

  return true;
});



  // Fly to marker on click
  const handleMarkerClick = useCallback((event) => {
    setSelectedEvent(event);
    setViewState(prev => ({
      ...prev,
      latitude: event.lat,
      longitude: event.lng,
      zoom: 8,
      pitch: 0,
      bearing: 0
    }));
  }, []);

  // Close popup and reset view
  const closePopup = useCallback(() => {
    setSelectedEvent(null);

    // Reset to active events view or default view
    const activeLegend = legendItems.find(l => l.id === 'active_events');
    if (activeLegend && activeLegend.count > 0) {
        zoomToLegendMarkers(activeLegend);
    } else {
        // Default US view if no active events
        setViewState(prev => ({
            ...prev,
            latitude: 39.8283,
            longitude: -98.5795,
            zoom: 3.5,
            pitch: 0,
            bearing: 0
        }));
    }
  }, [legendItems, zoomToLegendMarkers]);

  return (
    <div className="p-6 flex flex-col gap-6 overflow-y-auto min-h-full pb-20 dark:gif-bg">
      {/* Header */}
      <PageTitle
        title={"Dashboard"}
        description={"Welcome back! Here's your operations overview."}
      />

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            onClick={stat.onClick}
            className="bg-card p-4 rounded-xl border border-border shadow-sm flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer"
          >
            <div>
              <p className="text-muted-foreground text-sm font-medium mb-1">
                {stat.label}
              </p>
              <h3 className="text-2xl font-bold text-foreground">
                {stat.value}
              </h3>
            </div>
            <div className={`p-3 rounded-lg ${stat.bg}`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Map Section */}
      <div
        className="bg-card rounded-2xl border border-border shadow-sm relative overflow-hidden"
        style={{ height: "600px", minHeight: "600px" }}
      >
        {/* Map Controls Overlay - Top Bar */}
        <div className="absolute top-4 left-4 right-4 z-10 flex flex-col md:flex-row gap-3 justify-between items-start md:items-center pointer-events-none">
          <div className="flex gap-3 w-full md:w-auto pointer-events-auto">
            <div className="relative flex-1 md:w-80 bg-card rounded-lg shadow-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search shoots ...."
                // Added h-11 for consistent height
                className="pl-9 h-11 bg-card border-border focus-visible:ring-1"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <X
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 cursor-pointer hover:text-foreground"
                  onClick={() => setSearchQuery("")}
                />
              )}
            </div>
          </div>

          <div className="flex gap-3 w-full md:w-auto pointer-events-auto">
            <Select value={eventFilter} onValueChange={setEventFilter}>
              {/* Added h-11 for consistent height */}
              <SelectTrigger className="w-[140px] h-11 bg-card text-foreground border-border">
                <SelectValue placeholder="Filter shoots" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">All</SelectItem>
                <SelectItem value="1">Brand Campaign</SelectItem>
                <SelectItem value="2">Product Photography</SelectItem>
                <SelectItem value="3">Lifestyle</SelectItem>
                <SelectItem value="4">Event Coverage</SelectItem>
                <SelectItem value="5">Portrait Session</SelectItem>
                <SelectItem value="6">Commercial</SelectItem>
                <SelectItem value="7">Livestream</SelectItem>
              </SelectContent>
            </Select>

            <Select value={crewFilter} onValueChange={setCrewFilter}>
              {/* Added h-11 for consistent height */}
              <SelectTrigger className="w-[120px] h-11 bg-card text-foreground border-border">
                <SelectValue placeholder="Crew" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_crew">All</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                {/* Added h-11 for consistent height */}
                <Button
                  variant="outline"
                  className="h-11 bg-card text-foreground border-border gap-2"
                >
                  <CalendarDays size={16} />
                  <span>
                    {selectedDate
                      ? format(new Date(selectedDate), "MMM d, yyyy")
                      : "All Dates"}
                  </span>
                  {selectedDate && (
                    <X
                      size={14}
                      className="ml-1 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDate(null);
                      }}
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate ? new Date(selectedDate) : undefined}
                  onSelect={(date) => setSelectedDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Map Component */}
        {isValidToken ? (
          <Map
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            style={{ width: "100%", height: "100%" }}
            mapStyle={
              theme === "dark"
                ? "mapbox://styles/mapbox/dark-v11"
                : "mapbox://styles/mapbox/light-v11"
            }
            mapboxAccessToken={MAPBOX_TOKEN}
            onClick={() => setSelectedEvent(null)}
            projection="globe"
            fog={{
              color:
                theme === "dark" ? "rgb(20, 20, 30)" : "rgb(255, 255, 255)",
              "high-color":
                theme === "dark" ? "rgb(30, 30, 50)" : "rgb(200, 200, 255)",
              "horizon-blend": 0.1,
              "space-color":
                theme === "dark" ? "rgb(10, 10, 20)" : "rgb(10, 10, 20)",
              "star-intensity": 0.8,
            }}
          >
            <NavigationControl position="bottom-right" />

            {filteredEvents.map((event) => {
              const colors = getMarkerColor(event.status);
              const isHovered = hoveredMarker === event.id;
              const isSelected = selectedEvent?.id === event.id;
              const isHighlighted =
                highlightedLegend &&
                legendItems.find((l) => l.id === highlightedLegend)?.filter
                  .status === event.status;
              const isGeocoded = event.isGeocoded === true;

              return (
                <Marker
                  key={event.id}
                  latitude={event.lat}
                  longitude={event.lng}
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    handleMarkerClick(event);
                  }}
                >
                  <div
                    className={`
                      ${
                        isGeocoded ? "bg-gray-900/60" : "bg-gray-900/80"
                      } p-1.5 rounded-full shadow-lg
                      ${isGeocoded ? "border-2 border-dashed" : "border-2"} ${
                      colors.border
                    }
                      cursor-pointer transition-all duration-200
                      ${
                        isHovered || isSelected
                          ? "scale-125 shadow-xl"
                          : "hover:scale-110"
                      }
                      ${
                        isHighlighted
                          ? "scale-150 ring-2 ring-white ring-offset-2 ring-offset-gray-900"
                          : ""
                      }
                      ${isGeocoded ? "opacity-80" : ""}
                    `}
                    onMouseEnter={() => setHoveredMarker(event.id)}
                    onMouseLeave={() => setHoveredMarker(null)}
                  >
                    <div
                      className={`w-3 h-3 ${colors.bg} rounded-full ${
                        isSelected || isHighlighted ? "animate-pulse" : ""
                      }`}
                    />
                  </div>

                  {/* Hover tooltip */}
                  {isHovered && !isSelected && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap shadow-lg">
                      <p className="font-semibold">{event.title}</p>
                      <p className="text-gray-300">
                        {event.type === "crew" ? event.role : event.location}
                      </p>
                      {isGeocoded && (
                        <p className="text-yellow-400 text-[10px] mt-0.5">
                          📍 Approximate location
                        </p>
                      )}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
                    </div>
                  )}
                </Marker>
              );
            })}
          </Map>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-500">
            <MapPin size={48} className="mb-4 opacity-20" />
            <h3 className="font-semibold text-lg text-gray-700">
              Map Configuration Required
            </h3>
            <p className="max-w-md text-center mt-2 text-sm">
              Please configure your Mapbox Access Token in the <code>.env</code>{" "}
              file or in <code>src/pages/DashboardPage.js</code> to view the
              map.
            </p>
            <div className="mt-6 p-4 bg-white border border-gray-200 rounded-lg text-xs font-mono text-gray-600 max-w-md overflow-auto">
              REACT_APP_MAPBOX_TOKEN=pk.ey...
            </div>
          </div>
        )}

        {/* Event Popup Card */}
        {selectedEvent && (
          <div className="absolute top-1/2 right-6 -translate-y-1/2 z-20 w-80 bg-card rounded-xl shadow-2xl border border-border overflow-hidden animate-in slide-in-from-right-5 duration-300">
            <button
              onClick={closePopup}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors z-10"
            >
              <X size={16} className="text-muted-foreground" />
            </button>

            <div className="p-4 border-b border-border">
              <div className="flex justify-between items-start mb-3 pr-6">
                <span
                  className={`px-2 py-1 text-xs font-bold rounded-md uppercase tracking-wide ${
                    selectedEvent.status === "active"
                      ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                      : selectedEvent.status === "completed"
                      ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400"
                      : selectedEvent.status === "assigned"
                      ? "bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
                      : "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                  }`}
                >
                  {selectedEvent.status}
                </span>
                {selectedEvent.type === "event" && (
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-[10px] font-bold text-muted-foreground"
                      >
                        {i === 5 ? "+" : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <h3 className="font-bold text-foreground mb-2">
                {selectedEvent.title}
              </h3>

              {selectedEvent.type === "crew" ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {selectedEvent.role}
                  </p>
                  <div className="flex items-center gap-2 text-muted-foreground text-xs">
                    <MapPin size={14} />
                    <span>{selectedEvent.location}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <CalendarIcon size={14} />
                      <span>{selectedEvent.date}</span>
                      <MapPin size={14} className="ml-2" />
                      <span>{selectedEvent.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <Clock size={14} />
                      <span>{selectedEvent.time}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">
                      Equipment Assigned
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {selectedEvent.equipment?.map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1.5 px-2 py-1.5 bg-muted rounded-md border border-border"
                        >
                          <item.icon
                            size={12}
                            className="text-muted-foreground"
                          />
                          <span className="text-xs text-foreground font-medium">
                            {item.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="bg-muted/50 p-3 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {selectedEvent.requirements && (
                  <>
                    <span className="font-medium text-foreground">
                      Requirement ({selectedEvent.requirements})
                    </span>
                    <span className="mx-1">•</span>
                  </>
                )}
                Updated {selectedEvent.updated}
              </div>
              <Button
                size="sm"
                className="text-xs h-8 gap-1"
                onClick={() => navigate(`/project/${selectedEvent.id}`)}
              >
                View Details
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* Interactive Status Legend - Bottom Left */}
        <div className="absolute bottom-4 left-4 z-10 bg-gray-900/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-700 w-56">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-white text-sm">Status Legend</h4>
            <div className="flex gap-1">
              <button
                onClick={selectAllFilters}
                className="text-[10px] text-indigo-400 hover:text-indigo-300 font-medium px-1.5 py-0.5 rounded hover:bg-indigo-900/50 transition-colors"
              >
                All
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={clearAllFilters}
                className="text-[10px] text-gray-400 hover:text-gray-300 font-medium px-1.5 py-0.5 rounded hover:bg-gray-700 transition-colors"
              >
                None
              </button>
            </div>
          </div>
          <div className="space-y-1">
            {legendItems.map((item) => {
              const isActive = activeFilters.has(item.id);
              const isHighlighted = highlightedLegend === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => zoomToLegendMarkers(item)}
                  className={`
                    w-full flex items-center justify-between text-xs p-2 rounded-lg transition-all duration-200
                    ${
                      isHighlighted
                        ? "bg-indigo-600 ring-2 ring-indigo-400"
                        : ""
                    }
                    ${
                      isActive && !isHighlighted
                        ? "bg-gray-800 hover:bg-gray-700"
                        : !isHighlighted
                        ? "opacity-40 hover:opacity-70 bg-transparent"
                        : ""
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-sm ${
                        item.color
                      } transition-transform ${
                        isActive ? "scale-100" : "scale-75"
                      } ${isHighlighted ? "animate-pulse" : ""}`}
                    />
                    <span
                      className={`${
                        isActive ? "text-gray-200 font-medium" : "text-gray-500"
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`font-semibold ${
                        isActive ? "text-white" : "text-gray-500"
                      }`}
                    >
                      {item.count}
                    </span>
                    <MapPin
                      size={10}
                      className={`${
                        isActive ? "text-gray-400" : "text-gray-600"
                      }`}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Filter status indicator */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <p className="text-[10px] text-gray-500 text-center">
              Click to zoom • Showing {filteredEvents.length} of{" "}
              {dbEvents.filter((e) => e.hasCoordinates).length} mapped
            </p>
            <p className="text-[10px] text-gray-400 text-center mt-1">
              {dbEvents.length} total projects
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Events (Left - Span 2) */}
        <div
          ref={eventsSectionRef}
          className="lg:col-span-2 bg-card rounded-xl border border-border shadow-sm flex flex-col"
        >
          <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-foreground">
              Browse All Your Events
            </h3>
          </div>
          {/* Filters Toolbar */}
          <div className="p-4 border-b border-border bg-muted/20 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search events or crew..."
                className="pl-9 bg-background h-10 text-sm"
                value={eventSearchQuery}
                onChange={(e) => setEventSearchQuery(e.target.value)}
              />
              {eventSearchQuery && (
                <X
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 cursor-pointer hover:text-foreground"
                  onClick={() => setEventSearchQuery("")}
                />
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto">
              <Select
                value={eventCategoryFilter}
                onValueChange={setEventCategoryFilter}
              >
                <SelectTrigger className="w-[130px] bg-white dark:bg-background dark:border-muted-foreground border-none shadow-sm h-10 text-gray-900 text-muted-foreground">
                  <SelectValue placeholder="By Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">Brand Campaign</SelectItem>
                  <SelectItem value="2">Product Photography</SelectItem>
                  <SelectItem value="3">Lifestyle</SelectItem>
                  <SelectItem value="4">Event Coverage</SelectItem>
                  <SelectItem value="5">Portrait Session</SelectItem>
                  <SelectItem value="6">Commercial</SelectItem>
                  <SelectItem value="7">Livestream</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={eventStatusFilter}
                onValueChange={handleStatusFilterChange}
              >
                <SelectTrigger className="w-[130px] bg-white dark:bg-background dark:border-muted-foreground border-none shadow-sm h-10 text-gray-900 text-muted-foreground">
                  <SelectValue placeholder="By Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className="h-10 bg-background px-3 text-muted-foreground"
                onClick={handleTodayFilter}
              >
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                Today
              </Button>
            </div>
          </div>

          {/* Events List */}
          <div className="flex-1 p-6 space-y-4 overflow-y-auto max-h-[700px]">
            {" "}
            {/* Add max-h-[400px] for fixed height */}
            {/* Loading State */}
            {upcomingEventsLoading && (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="p-4 rounded-lg border border-border bg-background/50 animate-pulse"
                  >
                    <div className="h-5 bg-muted rounded w-1/3 mb-3"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            )}
            {/* Error State */}
            {upcomingEventsError && !upcomingEventsLoading && (
              <div className="text-center py-10">
                <p className="text-destructive text-sm">
                  {upcomingEventsError}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={loadUpcomingEvents}
                >
                  Retry
                </Button>
              </div>
            )}
            {/* Events List */}
            {!upcomingEventsLoading &&
              !upcomingEventsError &&
              upcomingEvents.length > 0 &&
              upcomingEvents.map((event) => {
                const eventDate = event.event_date
                  ? format(new Date(event.event_date), "MMM d, yyyy")
                  : "Date TBD";
                const eventTime = event.duration_hours
                  ? `${event.duration_hours} hours`
                  : "Duration TBD";
                const eventLocation = event.event_location || "Location TBD";

                return (
                  <div
                    key={event.stream_project_booking_id}
                    className="group flex flex-col md:flex-row gap-4 p-4 rounded-lg border border-border hover:bg-accent/5 hover:border-accent/50 transition-all bg-background/50"
                  >
                    {/* Left Side: Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-base text-foreground">
                          {event.project_name || "Untitled Project"}
                        </h4>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full
                ${
                  event.status === "active"
                    ? "bg-green-500/10 text-green-500"
                    : event.status === "pending"
                    ? "bg-yellow-500/10 text-yellow-500"
                    : "bg-blue-500/10 text-blue-500"
                }`}
                        >
                          {event.status || "pending"}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground mb-3">
                        <div className="flex items-center gap-1.5">
                          <CalendarIcon size={14} />
                          <span>{eventDate}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin size={14} />
                          <span>{eventLocation}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground/80">
                        <Clock size={12} />
                        <span>{eventTime}</span>
                      </div>
                    </div>

                    {/* Right Side: Actions */}
                    <div className="flex md:flex-col items-center md:items-end justify-between gap-3 min-w-[100px]">
                      <Button
                        className="w-full bg-[#1D1D1B] text-[#ECE1CE] hover:bg-black dark:bg-white dark:text-black dark:hover:bg-gray-200 h-8 text-xs font-medium"
                        onClick={() =>
                          navigate(
                            `/project/${event.stream_project_booking_id}`
                          )
                        }
                      >
                        {event.status === "pending"
                          ? "Confirm"
                          : "View Details"}
                      </Button>
                      <button
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                        onClick={() =>
                          navigate(
                            `/project/${event.stream_project_booking_id}`
                          )
                        }
                      >
                        View Details <ChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            {/* Empty State */}
            {!upcomingEventsLoading &&
              !upcomingEventsError &&
              upcomingEvents.length === 0 && (
                <div className="text-center py-10 text-muted-foreground">
                  <CalendarIcon className="mx-auto mb-3 w-12 h-12 opacity-20" />
                  <p className="font-medium">No upcoming events found</p>
                  <p className="text-sm mt-1">
                    Try adjusting your filters or create a new project
                  </p>
                </div>
              )}
          </div>

          {/* <div className="p-4 border-t border-border bg-muted/10">
             <Button variant="ghost" className="w-full text-xs font-medium text-muted-foreground hover:text-foreground h-9">
                View All Requirements
             </Button>
          </div> */}
        </div>

        {/* Recent Activity (Right - Span 1) */}
        <div className="bg-card rounded-xl border border-border shadow-sm flex flex-col h-fit">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">
              Recent Activity
            </h3>
          </div>

          <div className="p-6">
            {/* Loading State */}
            {recentActivityLoading && (
              <div className="space-y-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex gap-4 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-muted"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-2/3"></div>
                      <div className="h-3 bg-muted rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {recentActivityError && !recentActivityLoading && (
              <div className="text-center py-8">
                <p className="text-destructive text-sm">
                  {recentActivityError}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={loadRecentActivity}
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Activity List */}
            {!recentActivityLoading &&
              !recentActivityError &&
              recentActivityData.length > 0 && (
                <div className="relative space-y-8">
                  {/* Vertical Line */}
                  <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-border/50" />

                  {recentActivityData.map((item) => {
                    const iconConfig = getActivityIcon(item.type);
                    const IconComponent = iconConfig.icon;

                    return (
                      <div key={item.id} className="relative flex gap-4">
                        {/* Icon Bubble */}
                        <div
                          className={`relative z-10 flex-none w-10 h-10 rounded-full ${iconConfig.bg} border border-border flex items-center justify-center`}
                        >
                          <IconComponent
                            size={18}
                            className={iconConfig.color}
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 pt-1">
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="text-sm font-medium text-foreground">
                              {item.title}
                            </h4>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                              {item.time}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            {/* Empty State */}
            {!recentActivityLoading &&
              !recentActivityError &&
              recentActivityData.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="mx-auto mb-3 w-12 h-12 opacity-20" />
                  <p className="font-medium">No recent activity</p>
                  <p className="text-sm mt-1">
                    Activity will appear here as you work
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
